import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

interface CollectionItem {
  title: string;
  description: string;
  href: string;
  accent: string;
  image?: string;
}

interface CollectionShowcaseProps {
  collections: CollectionItem[];
}

const Section = styled.section`
  padding: clamp(70px, 11vw, 130px) 0;
  background: linear-gradient(180deg, #ffffff 0%, #f7f3ee 100%);
`;

const Inner = styled.div`
  display: grid;
  gap: clamp(32px, 6vw, 56px);
`;

const Header = styled.div`
  display: grid;
  gap: 12px;
  max-width: 620px;
`;

const Title = styled.h2`
  font-size: clamp(1.95rem, 3vw, 2.6rem);
  font-weight: 600;
  color: #2c2a29;
  letter-spacing: -0.01em;
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  line-height: 1.7;
  color: rgba(44, 42, 41, 0.7);
`;

const Carousel = styled.div`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(260px, 320px);
  gap: 20px;
  overflow-x: auto;
  padding-bottom: 8px;
  scrollbar-width: thin;

  &::-webkit-scrollbar {
    height: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(47, 58, 52, 0.35);
    border-radius: 999px;
  }

  @media (max-width: 768px) {
    grid-auto-columns: minmax(240px, 280px);
  }
`;

const Card = styled(Link)`
  position: relative;
  display: grid;
  gap: 18px;
  border-radius: 28px;
  background: #ffffff;
  color: #1f221f;
  text-decoration: none;
  border: 1px solid rgba(47, 58, 52, 0.08);
  overflow: hidden;
  box-shadow: 0 18px 36px rgba(37, 45, 40, 0.18);
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 28px 56px rgba(37, 45, 40, 0.22);
  }
`;

const CardImage = styled.div<{ image?: string; accent: string }>`
  position: relative;
  width: 100%;
  aspect-ratio: 5 / 4;
  background: ${({ image, accent }) =>
    image
      ? `linear-gradient(160deg, rgba(23, 28, 25, 0.4), rgba(23, 28, 25, 0.1)), url('${image}') center/cover`
      : accent};
`;

const CardBody = styled.div`
  display: grid;
  gap: 12px;
  padding: 0 24px 26px;
`;

const CardTitle = styled.h3`
  font-size: 1.35rem;
  font-weight: 600;
  margin: 0;
`;

const CardDescription = styled.p`
  font-size: 0.95rem;
  line-height: 1.6;
  color: rgba(31, 34, 31, 0.7);
  margin: 0;
`;

const CardMeta = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: rgba(31, 34, 31, 0.6);
  margin-top: auto;
`;

const CollectionShowcase: React.FC<CollectionShowcaseProps> = ({ collections }) => {
  if (!collections.length) {
    return null;
  }

  return (
    <Section>
      <div className="container">
        <Inner>
          <Header>
            <Title>Kuraterade teman för varje stund</Title>
            <Subtitle>
              Låt dig inspireras av våra florist- och inredningskoncept. Eleganta kombinationer färdiga
              att klicka hem – för vardagsrum, helgdukning och omtänksamma presenter.
            </Subtitle>
          </Header>

          <Carousel>
            {collections.map(collection => (
              <Card key={collection.title} to={collection.href}>
                <CardImage image={collection.image} accent={collection.accent} />
                <CardBody>
                  <CardTitle>{collection.title}</CardTitle>
                  <CardDescription>{collection.description}</CardDescription>
                  <CardMeta>Visa kollektion</CardMeta>
                </CardBody>
              </Card>
            ))}
          </Carousel>
        </Inner>
      </div>
    </Section>
  );
};

export type { CollectionItem };
export default CollectionShowcase;
